import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SimpleRoutine from './SimpleRoutine';
import { CheckCircle, Sun, Clock, CloudRain, Wind, Drop, Fire, WarningCircle, Leaf, Syringe, CalendarBlank, Thermometer, Users, User, PaperPlaneRight, Check, Sparkle, Scan } from '@phosphor-icons/react';
import LoadingOverlay from '../components/LoadingOverlay';
import { workerFetch } from '../utils/apiUtils';
import { useScrollRestoration, ensureScrollable } from '../utils/scrollUtils';

// Moved questionnaire outside so it's stable and doesn't cause re-renders of useCallback
const questionnaire = [
  { id: 'intro', type: 'bot', text: 'سلام! من Dermowise هستم، دستیار هوشمند شما برای مراقبت از پوست. برای اینکه بهترین روتین رو براتون طراحی کنم، چند تا سوال کوتاه دارم. آماده‌اید؟' },
  { id: 'start_confirm', type: 'user_options', options: [{ label: 'بله، شروع کنیم!', value: 'start' }] },
  // بخش ۱
  { id: 'q_skinType', type: 'bot', text: 'عالی! اول بگید نوع پوستتون چیه؟' },
  {
    id: 'skinType', type: 'user_options', field: 'skinType',
    options: [
      { label: 'خشک', value: 'dry', subLabel: 'کشیدگی، پوسته‌پوسته شدن، کم‌آبی' },
      { label: 'چرب', value: 'oily', subLabel: 'برق افتادن، احساس سنگینی، جوش' },
      { label: 'مختلط', value: 'combination', subLabel: 'چرب در ناحیه T، خشک در گونه‌ها' },
      { label: 'نرمال', value: 'normal', subLabel: 'تعادل، بدون خشکی یا چربی زیاد' },
      { label: 'حساس', value: 'sensitive', subLabel: 'واکنش سریع، قرمزی، خارش' }
    ]
  },
  { id: 'q_skinReaction', type: 'bot', text: 'پوستتون معمولاً چطور واکنش نشون میده؟' },
  {
    id: 'skinReaction', type: 'user_options', field: 'skinReaction',
    options: [
      { label: 'خیلی راحت قرمز میشه', value: 'redness_easily' },
      { label: 'به بعضی محصولات می‌سوزه یا می‌خاره', value: 'burns_itches' },
      { label: 'واکنش خاصی نداره', value: 'no_reaction' },
      { label: 'نمی‌دونم', value: 'dont_know_reaction' }
    ]
  },
   { id: 'q_skinTone', type: 'bot', text: 'و رنگ یا تناژ پوستتون به کدوم نزدیک‌تره؟' },
   {
    id: 'skinTone', type: 'user_options', field: 'skinTone',
    options: [
       { label: 'خیلی روشن', value: 'very_light' },
       { label: 'روشن', value: 'light' },
       { label: 'گندمی (مایل به زرد)', value: 'medium_yellowish' },
       { label: 'سبزه (مایل به زیتونی)', value: 'olive_tan' },
       { label: 'تیره', value: 'dark' }
     ]
   },
  // بخش ۲
  { id: 'q_skinConcerns', type: 'bot', text: 'چه مشکلاتی دارید یا دوست دارید روی چی کار کنیم؟ (چند مورد)' },
  {
    id: 'skinConcerns', type: 'user_multi_options', field: 'skinConcerns',
     options: [
       { label: 'جوش فعال', value: 'active_acne' },
       { label: 'جای جوش / لک', value: 'pih' },
       { label: 'تیرگی / کدری', value: 'dullness' },
       { label: 'چین و چروک', value: 'wrinkles_finelines' },
       { label: 'منافذ باز / بافت ناهموار', value: 'enlarged_pores_texture' },
       { label: 'خشکی / کشیدگی', value: 'persistent_dryness' },
       { label: 'التهاب / قرمزی / حساسیت', value: 'inflammation_redness_sensitivity' },
       { label: 'چربی / برق زیاد', value: 'excessive_oiliness' },
       { label: 'افتادگی / کاهش سفتی', value: 'sagging_skin_firmness' }
     ],
     concludeAction: true // Add a button to finish multi-select
   },
  // بخش ۳
  { id: 'q_currentProducts', type: 'bot', text: 'در حال حاضر از چه محصولاتی استفاده می‌کنید؟' },
  { id: 'currentProducts', type: 'user_text_input', field: 'currentProducts', placeholder: 'نام و برند محصولات...' },
  { id: 'q_sensitivities', type: 'bot', text: 'به کدوم ترکیبات یا محصولات حساسیت داشتید؟ (چند مورد)' },
  {
    id: 'sensitivities', type: 'user_multi_options', field: 'sensitivities',
     options: [
       { label: 'الکل', value: 'alcohol' },
       { label: 'عطر (Fragrance)', value: 'fragrance' },
       { label: 'رتینول', value: 'retinol' },
       { label: 'ویتامین C', value: 'vitamin_c' },
       { label: 'AHA / BHA', value: 'aha_bha' },
       { label: 'سیلیکون', value: 'silicone' },
       { label: 'نمی‌دونم', value: 'dont_know_sensitivity' }
     ],
     concludeAction: true
   },
   { id: 'q_otherSensitivity', type: 'bot', text: 'حساسیت دیگه‌ای هم دارید که توی لیست نبود؟' },
   { id: 'otherSensitivity', type: 'user_text_input', field: 'otherSensitivity', placeholder: 'مثال: پارابن...' },
   { id: 'q_hasMedication', type: 'bot', text: 'تحت درمان پوستی یا داروی خاصی هستید؟' },
   {
    id: 'hasMedication', type: 'user_options', field: 'hasMedication',
     options: [ { label: 'بله', value: 'yes' }, { label: 'خیر', value: 'no' } ]
   },
   { id: 'q_medicationDetails', type: 'bot', text: 'لطفاً توضیح بدید چه دارو یا درمانی هست.', dependsOn: 'hasMedication', dependsValue: 'yes' },
   { id: 'medicationDetails', type: 'user_text_input', field: 'medicationDetails', placeholder: 'نام دارو یا درمان...', dependsOn: 'hasMedication', dependsValue: 'yes' },
  // بخش ۴
  { id: 'q_routineTime', type: 'bot', text: 'چه زمانی برای روتین پوستی وقت دارید؟' },
  {
    id: 'routineTime', type: 'user_options', field: 'routineTime',
    options: [
      { label: 'فقط صبح‌ها', value: 'morning_only' },
      { label: 'فقط شب‌ها', value: 'night_only' },
      { label: 'صبح و شب', value: 'morning_night' },
      { label: 'فقط ۵ دقیقه!', value: 'quick_5_min' },
      { label: 'وقت کافی دارم', value: 'full_routine_time' }
    ]
  },
  // بخش ۵
  { id: 'q_climate', type: 'bot', text: 'آب‌وهوای محل زندگیتون چطوره؟' },
  {
    id: 'climate', type: 'user_options', field: 'climate',
     options: [
       { label: 'گرم و خشک', value: 'hot_dry' },
       { label: 'گرم و مرطوب', value: 'hot_humid' },
       { label: 'سرد و خشک', value: 'cold_dry' },
       { label: 'سرد و مرطوب', value: 'cold_humid' },
       { label: 'معتدل', value: 'temperate' }
     ]
   },
   { id: 'q_sunExposure', type: 'bot', text: 'روزانه چقدر در معرض آفتاب هستید؟' },
   {
    id: 'sunExposure', type: 'user_options', field: 'sunExposure',
     options: [
       { label: 'کمتر از ۳۰ دقیقه', value: 'less_30_min' },
       { label: 'بین ۳۰ دقیقه تا ۲ ساعت', value: '30min_2hr' },
       { label: 'بیشتر از ۲ ساعت', value: 'more_2hr' }
     ]
   },
   { id: 'q_pollutedCity', type: 'bot', text: 'توی شهر آلوده زندگی می‌کنید؟' },
   {
    id: 'pollutedCity', type: 'user_options', field: 'pollutedCity',
     options: [ { label: 'بله', value: 'yes' }, { label: 'خیر', value: 'no' } ]
   },
  // Final confirmation
  { id: 'final_confirm', type: 'bot', text: 'خیلی هم عالی! تمام اطلاعات لازم رو گرفتم. آماده‌اید که درخواست روتین رو ثبت کنم؟' },
  { id: 'submit', type: 'user_options', options: [{ label: 'بله، ثبت کن!', value: 'submit' }] }
];

const STATIC_INITIAL_ANSWERS = {
  skinType: '', skinReaction: '', skinTone: '',
  skinConcerns: [],
  currentProducts: '', sensitivities: [], otherSensitivity: '',
  hasMedication: '', medicationDetails: '',
  routineTime: '',
  climate: '', sunExposure: '', pollutedCity: '',
  // budget: '', // Ensure this is part of your questionnaire data if used
  step: 1, // This refers to internal form step, not overall routineStatus step
};

const Routine = () => {
  const navigate = useNavigate();
  const { userId, token, userProfile } = useAuth();
  const chatEndRef = useRef(null);

  // همه هوک‌ها باید اینجا باشند
  const [isLoading, setIsLoading] = useState(true);
  const [routineStatus, setRoutineStatus] = useState('check');
  const [messages, setMessages] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [collectedAnswers, setCollectedAnswers] = useState(STATIC_INITIAL_ANSWERS);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [generatedRoutineData, setGeneratedRoutineData] = useState(null);
  const [isFetchingResult, setIsFetchingResult] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [scanFound, setScanFound] = useState(null); // null: unknown, true: found, false: not_found
  const [profileComplete, setProfileComplete] = useState(null); // null: unknown, true: complete, false: incomplete
  
  // Enable scroll restoration
  useScrollRestoration();

  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    if (!userId || !token) {
      throw new Error("User ID or auth token missing");
    }
    
    console.log(`[Routine API] Calling: ${endpoint}`);
    
    return workerFetch(endpoint, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      },
    });
  }, [userId, token]);

  // همه useCallback ها
  const resetQuestionnaireState = useCallback(() => {
    setCollectedAnswers(STATIC_INITIAL_ANSWERS);
    setCurrentQuestionIndex(0);
    setMessages([]);
    setIsBotTyping(false);
    if (userId) {
      localStorage.removeItem(`dermowiseRoutineAnswers_${userId}`);
      localStorage.removeItem(`dermowiseRoutineIndex_${userId}`);
    }
  }, [userId]);

  const resetAllAndGoToInitialCheck = useCallback(() => {
    if (userId) {
      localStorage.removeItem(`dermowiseRoutineStatus_${userId}`);
    }
    resetQuestionnaireState();
    setRoutineStatus('check');
    setGeneratedRoutineData(null);
  }, [userId, resetQuestionnaireState]);

  const askQuestion = useCallback((index) => {
    if (index < 0 || index >= questionnaire.length) {
      console.error(`[askQuestion] Invalid index: ${index}`);
      return;
    }
    const question = questionnaire[index];
    if (question.dependsOn && collectedAnswers[question.dependsOn] !== question.dependsValue) {
      askQuestion(index + 1);
      return;
    }
    if (question.type === 'bot') {
      setIsBotTyping(true);
      setMessages(prev => [...prev, { sender: 'bot', text: question.text }]);
      setTimeout(() => {
        setIsBotTyping(false);
        const nextIndex = index + 1;
        if (nextIndex < questionnaire.length) {
          askQuestion(nextIndex);
        }
      }, 800);
    } else {
      setCurrentQuestionIndex(index);
      if (isBotTyping) setIsBotTyping(false);
    }
  }, [collectedAnswers, isBotTyping]);

  const fetchRoutineResult = useCallback(async (isInitialLoad = false) => {
    console.log('🔍 fetchRoutineResult called (UPDATED v5):', { isInitialLoad, userId, isFetchingResult, scanFound });
    if (!userId || !fetchWithAuth) {
      console.log('❌ Missing userId or fetchWithAuth');
      return false;
    }
    if (isFetchingResult && !isInitialLoad) {
      console.log('❌ Already fetching result');
      return false;
    }
    if (scanFound === null || scanFound === false) {
      console.log('❌ No scan found yet, skipping routine fetch');
      return false;
    }
    setIsFetchingResult(true);
    try {
      console.log('🔍 Fetching routine result for user:', userId);
      console.log('🔍 About to call /api/routine/result...');
      const response = await fetchWithAuth('/api/routine/result', { method: 'GET' });
      console.log('🔍 Routine result response:', { response, ok: response.ok, status: response.status });
      if (response.ok) {
        const result = response; // workerFetch already parses JSON
        console.log('🔍 Routine result data:', result);
        if (result.found && result.data && Object.keys(result.data).length > 0) {
          console.log('✅ Routine found, setting data and status to completed');
          setGeneratedRoutineData(result.data);
          setRoutineStatus('completed');
          setIsFetchingResult(false);
          return true;
        } else {
          console.log('❌ No routine found, setting status to ready_to_request');
          setGeneratedRoutineData(null);
          setRoutineStatus('ready_to_request');
          setIsFetchingResult(false);
          return false;
        }
      } else {
        console.log('❌ Routine result response not ok:', response.status);
      }
    } catch (e) { 
      console.error("❌ fetchRoutineResult error:", e); 
    }
    finally { setIsFetchingResult(false); }
    return false;
  }, [userId, fetchWithAuth, isFetchingResult]);

  // --- Questionnaire Handlers ---
  // Start the questionnaire
  const startQuestionnaire = () => {
    setRoutineStatus('questionnaire');
    setMessages([{ sender: 'bot', text: questionnaire[0].text }]);
    setCurrentQuestionIndex(1); // Assuming first question is intro, then user options
  };

  // Handle confirmation (e.g., start or submit)
  const handleConfirmation = async (value) => {
    if (value === 'start') {
      askQuestion(currentQuestionIndex + 1);
    } else if (value === 'submit') {
      setIsSubmittingForm(true);
      try {
        const response = await fetchWithAuth('/api/routine/request', {
          method: 'POST',
          body: JSON.stringify(collectedAnswers),
        });
        if (response.ok) {
          setRoutineStatus('waiting');
          resetQuestionnaireState();
        } else {
          alert('خطا در ثبت درخواست روتین.');
        }
      } catch (e) {
        alert('خطا در ارتباط با سرور.');
      }
      setIsSubmittingForm(false);
    }
  };

  // Handle single-choice response
  const handleResponse = (field, value, label) => {
    setCollectedAnswers(prev => ({ ...prev, [field]: value }));
    setMessages(prev => [
      ...prev,
      { sender: 'user', text: label }
    ]);
    
    // Small delay before asking next question for better UX
    setTimeout(() => {
      askQuestion(currentQuestionIndex + 1);
    }, 300);
  };

  // Handle multi-select change
  const handleMultiChange = (field, value) => {
    setCollectedAnswers(prev => {
      const arr = prev[field] || [];
      return {
        ...prev,
        [field]: arr.includes(value)
          ? arr.filter(v => v !== value)
          : [...arr, value]
      };
    });
  };

  // Finalize multi-select and move on
  const handleMultiConclude = (field) => {
    const selected = collectedAnswers[field] || [];
    setMessages(prev => [
      ...prev,
      { sender: 'user', text: selected.map(val => {
        const opt = questionnaire[currentQuestionIndex].options.find(o => o.value === val);
        return opt ? opt.label : val;
      }).join(', ') }
    ]);
    askQuestion(currentQuestionIndex + 1);
  };

  // Handle text input submit
  const handleTextSubmit = (field, value) => {
    if (!value.trim()) return;
    setCollectedAnswers(prev => ({ ...prev, [field]: value.trim() }));
    setMessages(prev => [
      ...prev,
      { sender: 'user', text: value.trim() }
    ]);
    
    // Small delay before asking next question for better UX
    setTimeout(() => {
      askQuestion(currentQuestionIndex + 1);
    }, 300);
  };

  // همه useEffect ها
  useEffect(() => {
    // Log mount/unmount
    const instanceId = Date.now();
    return () => {
      // Log unmount
    };
  }, []);

  useEffect(() => {
    // Main logic for status transitions
    if (routineStatus === 'questionnaire' || routineStatus === 'waiting') return;
    setIsLoading(true);
    if (!userId) {
      resetAllAndGoToInitialCheck();
      setIsLoading(false);
      return;
    }

    if (!userProfile) {
      console.log('❌ No profile');
      setRoutineStatus('profile_incomplete');
      setIsLoading(false);
      return;
    }

    console.log('🔍 Raw userProfile:', userProfile);
    const isProfileComplete = userProfile && 
      typeof userProfile === 'object' && 
      userProfile.age && 
      (Array.isArray(userProfile.skinType) ? userProfile.skinType.length > 0 : !!userProfile.skinType);
    
    console.log('🔍 Profile check (UPDATED v16):', { 
      userProfile: !!userProfile, 
      isProfileComplete,
      age: userProfile?.age,
      skinType: userProfile?.skinType,
      routineStatus
    });

    if (!isProfileComplete) {
      console.log('❌ Incomplete profile');
      setRoutineStatus('profile_incomplete');
      setIsLoading(false);
      return;
    }

    console.log('🔍 Profile complete, fetching routine (UPDATED v15)');
    fetchWithAuth('/api/routine/result', { method: 'GET' })
      .then(response => {
        console.log('🔍 Routine result response (UPDATED v11):', response);
        if (!response) {
          console.log('❌ No response from API');
          return { found: false };
        }
        if (response.error) {
          console.log('❌ API returned error:', response.error);
          return { found: false };
        }
        if (response.found === false) {
          console.log('❌ API explicitly returned found: false');
          return { found: false };
        }
        if (response.data) {
          console.log('✅ Found routine data in response.data');
          return { found: true, data: response.data };
        }
        if (typeof response === 'object' && Object.keys(response).length > 0) {
          console.log('✅ Using response itself as data');
          return { found: true, data: response };
        }
        console.log('❌ No valid data structure in response');
        return { found: false };
      })
      .then(routineResult => {
        console.log('🔍 Routine result (UPDATED v10):', routineResult);
        if (routineResult.found && routineResult.data) {
          console.log('✅ Setting routine data:', routineResult.data);
          console.log('✅ Data type:', typeof routineResult.data);
          console.log('✅ Data keys:', Object.keys(routineResult.data));
          setGeneratedRoutineData(routineResult.data);
          setRoutineStatus('completed');
          setIsLoading(false);
        } else {
          console.log('❌ No routine found, setting status to questionnaire');
          console.log('❌ Result:', routineResult);
          setRoutineStatus('questionnaire');
          setIsLoading(false);
        }
      })
      .catch(error => {
        console.error('❌ Error fetching routine:', error);
        setRoutineStatus('error');
        setIsLoading(false);
      });
  }, [userProfile, userId, routineStatus, fetchWithAuth, resetAllAndGoToInitialCheck]);

  useEffect(() => {
    if (userId && routineStatus !== 'check') {
      localStorage.setItem(`dermowiseRoutineStatus_${userId}`, routineStatus);
    }
  }, [routineStatus, userId]);

  useEffect(() => {
    if (userId && routineStatus === 'questionnaire') {
      localStorage.setItem(`dermowiseRoutineAnswers_${userId}`, JSON.stringify(collectedAnswers));
      localStorage.setItem(`dermowiseRoutineIndex_${userId}`, currentQuestionIndex.toString());
    }
  }, [collectedAnswers, currentQuestionIndex, routineStatus, userId]);

  useEffect(() => {
    let intervalId = null;
    if (routineStatus === 'waiting' && userId && !isFetchingResult) {
      fetchRoutineResult();
      intervalId = setInterval(fetchRoutineResult, 15000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [routineStatus, userId, isFetchingResult, fetchRoutineResult]);

  useEffect(() => {
    // Gentle scroll to make new content visible without aggressive jumping
    setTimeout(() => {
      if (chatEndRef.current) {
        chatEndRef.current.scrollIntoView({ 
          behavior: "smooth", 
          block: "nearest",  // Less aggressive than "end"
          inline: "nearest"
        });
      }
    }, 100); // Slightly longer delay for smoother UX
  }, [messages, isBotTyping]);

  // ... سایر توابع و منطق روتین ...

  // فقط در بخش رندر شرط بگذار
  if (!userId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div>در حال بارگذاری اطلاعات کاربر...</div>
      </div>
    );
  }

  // ... بقیه رندر اصلی ...

  return (
    <div className="min-h-screen bg-gray-100">
      <LoadingOverlay 
        isLoading={routineStatus === 'waiting' || isSubmittingForm} 
        message={
          routineStatus === 'waiting' ? 'در حال تحلیل اطلاعات و ایجاد روتین...' :
          isSubmittingForm ? 'در حال ارسال درخواست...' :
          'در حال بارگذاری...'
        }
      />
      {isLoading ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin h-12 w-12 border-4 border-black border-t-transparent rounded-full"></div>
        </div>
      ) : (
        <div className="container mx-auto px-4 py-8">
          {routineStatus === 'profile_incomplete' && (
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <WarningCircle size={48} className="mx-auto mb-4 text-yellow-500" />
              <h2 className="text-2xl font-bold mb-4">پروفایل ناقص است</h2>
              <p className="mb-6">لطفاً ابتدا پروفایل خود را تکمیل کنید.</p>
              <button
                onClick={() => navigate('/profile')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                تکمیل پروفایل
              </button>
            </div>
          )}

          {routineStatus === 'scan_incomplete' && (
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <Scan size={48} className="mx-auto mb-4 text-blue-500" />
              <h2 className="text-2xl font-bold mb-4">اسکن پوستی نیاز است</h2>
              <p className="mb-6">برای دریافت روتین شخصی‌سازی شده، لطفاً ابتدا اسکن پوستی انجام دهید.</p>
              <button
                onClick={() => navigate('/scanpage')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                انجام اسکن
              </button>
            </div>
          )}

          {routineStatus === 'ready_to_request' && (
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <Sparkle size={48} className="mx-auto mb-4 text-purple-500" />
              <h2 className="text-2xl font-bold mb-4">آماده دریافت روتین هستید!</h2>
              <p className="mb-6">با کلیک روی دکمه زیر، به سوالات کوتاهی پاسخ دهید تا روتین شخصی‌سازی شما را آماده کنیم.</p>
              <button
                onClick={startQuestionnaire}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                شروع پرسشنامه
              </button>
            </div>
          )}

          {routineStatus === 'questionnaire' && (
            <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md overflow-hidden mb-20">
              <div className="h-[calc(100vh-16rem)] flex flex-col">
                <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-24">
                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.sender === 'bot' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          msg.sender === 'bot'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-blue-600 text-white'
                        }`}
                      >
                        {msg.sender === 'bot' && (
                          <div className="flex items-center mb-2">
                            <img src="/bot.png" alt="Bot" className="w-6 h-6 mr-2" />
                            <span className="font-semibold">Dermowise</span>
                          </div>
                        )}
                        <p className="whitespace-pre-wrap">{msg.text}</p>
                      </div>
                    </div>
                  ))}

                  {/* USER INPUT AREA - Rendered as a "message" block */}
                  {!isBotTyping && currentQuestionIndex < questionnaire.length && questionnaire[currentQuestionIndex].type.startsWith('user_') && (
                    <div className="mt-3"> {/* Spacing for the input block */}
                      {(() => {
                        const question = questionnaire[currentQuestionIndex];
                        if (!question) return null;

                        switch (question.type) {
                          case 'user_options':
                            return (
                              <div className="space-y-3 flex flex-col items-end">
                                {question.options.map((option) => (
                                  <button
                                    key={option.value}
                                    onClick={() => {
                                      if (question.id === 'start_confirm' || question.id === 'submit') {
                                        handleConfirmation(option.value);
                                      } else {
                                        handleResponse(question.field, option.value, option.label);
                                      }
                                    }}
                                    className="w-auto max-w-[80%] bg-blue-500 text-white text-right p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-md min-h-[48px] flex items-center justify-center"
                                    disabled={isSubmittingForm && question.id === 'submit'}
                                  >
                                    {isSubmittingForm && question.id === 'submit' ? (
                                      <span className="flex items-center justify-center">
                                        <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                                        در حال ثبت درخواست...
                                      </span>
                                    ) : (
                                      <>
                                        {option.label}
                                        {option.subLabel && (
                                          <div className="text-sm text-blue-100 mt-1 font-normal">{option.subLabel}</div>
                                        )}
                                      </>
                                    )}
                                  </button>
                                ))}
                              </div>
                            );
                          case 'user_multi_options':
                            return (
                              <div className="space-y-2 flex flex-col items-end">
                                {question.options.map((option) => (
                                  <button
                                    key={option.value}
                                    onClick={() => handleMultiChange(question.field, option.value)}
                                    className={`w-auto max-w-[85%] text-right p-3 rounded-lg border transition-colors shadow ${
                                      collectedAnswers[question.field]?.includes(option.value)
                                        ? 'bg-blue-600 text-white border-blue-700'
                                        : 'bg-white text-blue-600 border-gray-300 hover:bg-gray-50'
                                    }`}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                                {question.concludeAction && (
                                  <button
                                    onClick={() => handleMultiConclude(question.field)}
                                    className="w-auto max-w-[85%] mt-3 bg-green-500 text-white p-3 rounded-lg hover:bg-green-600 transition-colors shadow self-end"
                                  >
                                    ادامه
                                  </button>
                                )}
                              </div>
                            );
                          case 'user_text_input':
                            return (
                              <form
                                onSubmit={(e) => {
                                  e.preventDefault();
                                  const inputElement = e.target.elements[question.field];
                                  handleTextSubmit(question.field, inputElement.value);
                                  inputElement.value = ''; // Clear input after submit
                                }}
                                className="flex gap-3 justify-end items-center"
                              >
                                <input
                                  type="text"
                                  name={question.field}
                                  placeholder={question.placeholder || "پاسخ خود را بنویسید..."}
                                  className="flex-grow max-w-[calc(80%-4rem)] p-4 rounded-lg border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm min-h-[48px]"
                                  style={{ direction: 'rtl' }}
                                />
                                <button
                                  type="submit"
                                  className="bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-md flex items-center justify-center min-h-[48px] min-w-[48px]"
                                  aria-label="ارسال"
                                >
                                  <PaperPlaneRight size={20} weight="fill" />
                                </button>
                              </form>
                            );
                          default:
                            return null;
                        }
                      })()}
                    </div>
                  )}

                  {isBotTyping && (
                    <div className="flex justify-start mb-4">
                      <div className="bg-gray-100 rounded-lg p-4 max-w-[80%]">
                        <div className="flex items-center mb-3">
                          <img src="/bot.png" alt="Bot" className="w-6 h-6 mr-2" />
                          <span className="font-semibold">Dermowise</span>
                        </div>
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              </div>
            </div>
          )}

          {routineStatus === 'waiting' && (
            <div className="bg-white rounded-lg shadow-md p-8 text-center flex flex-col items-center justify-center min-h-[300px]">
              <div className="animate-spin h-16 w-16 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-6"></div>
              <h2 className="text-3xl font-bold mb-4 text-gray-800">هوش مصنوعی در حال تحلیل اطلاعات شماست...</h2>
              <p className="text-gray-600 text-lg mb-2">بر اساس پاسخ‌های شما، یک روتین اختصاصی در حال آماده‌سازی است.</p>
              <p className="text-gray-500 mt-2">این مرحله ممکن است تا ۶۰ ثانیه زمان ببرد. از شکیبایی شما سپاسگزاریم!</p>
            </div>
          )}

          {routineStatus === 'completed' && generatedRoutineData && (
            <SimpleRoutine routineData={generatedRoutineData} />
          )}

        </div>
      )}
    </div>
  );
};

export default Routine;